import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import SuccessAlert from 'store/SuccessAlert';
import ErrorAlert from 'store/ErrorAlert';
import { getToken } from 'store/token';
import Loader from 'store/Loader';
import { passwordRegex } from 'store/validationPatterns';
import { apiUrl } from 'store/constant';

const ChangePasswordDialog = ({ open, onClose, userId }) => {
    const token = getToken();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New password and confirm password do not match.');
            return;
        }
        if (!passwordRegex.test(newPassword)) {
            setError('Password must be at least 8 characters and include at least one special character');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put(
                `${apiUrl}/user/updateByEmail`,
                { userid: userId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'CurrentPassword': currentPassword,
                        'NewPassword': newPassword
                    }
                }
            );

            const data = response.data;
            if (data.cd === 0) {
                setSuccessMessage(data.description);
                handleClose();
            } else {
                setError(data.description);
            }
        } catch (err) {
            console.error('Error saving item:', err);
            setErrorMessage('Some issue occurred. Please check.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle
                    sx={{
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(255, 255, 255, 0.75)',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                    }}
                >
                    <LockOutlinedIcon color="primary" />
                    Change Password
                    <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.700' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        backdropFilter: 'blur(6px)',
                        background: 'rgba(250, 250, 250, 0.9)'
                    }}
                >
                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        label="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        label="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                        margin="normal"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {error && (
                        <Typography color="error" mt={1} fontSize="0.9rem">
                            {error}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        backdropFilter: 'blur(6px)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        p: 2
                    }}
                >
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Submit
                    </Button>
                    <Button onClick={handleClose} variant="outlined" color="error">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {successMessage && (
                <Box mb={2}>
                    <SuccessAlert message={successMessage} onClose={() => setSuccessMessage('')} duration={5000} />
                </Box>
            )}

            {errorMessage && (
                <Box mb={2}>
                    <ErrorAlert message={errorMessage} onClose={() => setErrorMessage('')} duration={3000} />
                </Box>
            )}

            {loading && <Loader />}
        </>
    );
};

export default ChangePasswordDialog;
